import {useQueryClient} from '@tanstack/react-query';
import {toast} from '@sakura-grp/ui/sonner';
import {useBusinessCardFieldChecks} from '@/hooks/useBusinessCardFieldChecks';
import {checkBusinessCardField} from '@/lib/api/business-cards';
import {BusinessCardFieldMark, BusinessCardFieldReason, needsFieldReview} from './business-card-field-status';
import {BizcardImageViewer} from './bizcard-image-viewer';
import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BottleWine } from 'lucide-react';
import { Constants } from '~/utils/constants';
import AppPicture from '~/components/react/AppPicture';
import type { CustomerDetailData, BusinessCard } from '~/types/customer-detail.types';
import { getStoreLogoMapping } from '@/lib/storage-utils';
import { formatPhoneNumber } from '@/lib/utils';
import { useHostessStore } from '@/stores/hostess';
import { HostessMemoEditSheet } from './hostess-memo-edit-sheet';
import { BusinessCardEditSheet } from './business-card-edit-sheet';

interface BaseCardsTabContentProps {
  data: CustomerDetailData;
}

export const BaseCardsTabContent = ({ data }: BaseCardsTabContentProps) => {
  const [memoDialogOpen, setMemoDialogOpen] = useState(false);
  const [viewerState, setViewerState] = useState<{
    front?: string;
    back?: string;
    initialSide: 'front' | 'back';
  } | null>(null);
  const [editCard, setEditCard] = useState<BusinessCard | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const { hostess } = useHostessStore();
  const fieldChecks = useBusinessCardFieldChecks(data.businessCards.map(card => String(card.id)));
  const queryClient = useQueryClient();
  const [checking, setChecking] = useState(false);
  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({queryKey: ['business-card-field-checks']}),
      queryClient.invalidateQueries({queryKey: ['customer-detail', data.id]}),
      queryClient.invalidateQueries({queryKey: ['tab-customers']}),
      queryClient.invalidateQueries({queryKey: ['tab-counts']}),
    ]);
  };
  const checkField = async (card: BusinessCard, field: 'name' | 'address', verdict: 'confirmed' | 'needs_review') => {
    const row = fieldChecks.data?.get(String(card.id));
    if (!hostess || !row || checking || fieldChecks.isFetching) return;
    setChecking(true);
    try {
      await checkBusinessCardField(String(card.id), field, verdict, row[`${field}_fingerprint`]);
      await invalidate();
    } catch (error) {
      if ((error as {code?: string}).code === 'BC102') await invalidate();
      toast.error(error instanceof Error ? error.message : '確認の状態を更新できませんでした');
    } finally {
      setChecking(false);
    }
  };
  const fieldReview = (card: BusinessCard, field: 'name' | 'address') => {
    const row = fieldChecks.data?.get(String(card.id));
    const disabled = !row || !row[`${field}_fingerprint`] || checking || fieldChecks.isFetching;
    return <>
      <BusinessCardFieldReason row={row} field={field} />
      {hostess && needsFieldReview(row, field) && <div className="flex flex-wrap gap-3 text-xs">
        <button type="button" className="text-brand disabled:opacity-50" disabled={disabled || !(field === 'name' ? card.name : card.companyAddress).trim()}
          onClick={() => void checkField(card, field, 'confirmed')}>画像と同じ</button>
        <button type="button" className="text-brand disabled:opacity-50" disabled={disabled}
          onClick={() => void checkField(card, field, 'needs_review')}>あとで見る</button>
        <button type="button" className="text-brand" onClick={() => {setEditCard(card); setEditOpen(true);}}>直す</button>
      </div>}
    </>;
  };

  const openViewer = useCallback((front?: string, back?: string, side: 'front' | 'back' = 'front') => {
    setViewerState({ front, back, initialSide: side });
  }, []);

  return (
    <div className="p-lg space-y-lg border-t">
      {/* 店舗別情報リスト */}
      <ul className="space-y-md">
        {data.stores.map((store) => (
          <li key={store.storeName} className="space-y-sm">
            <figure className="flex items-start">
              <AppPicture
                img={getStoreLogoMapping(store.storeLogo)}
                sources={[['default', {}]]}
                imgClass="h-lg w-full object-contain"
                alt={store.storeName}
                aria-hidden="true"
              />
            </figure>
            <div className="gap-md grid grid-cols-9 items-start">
              {/* 担当 */}
              <dl className="space-y-sm col-span-2">
                <dt className="text-xs font-bold text-gray-500">担当</dt>
                <dd>
                  {store.hostesses.length > 0 ? (
                    <ul className="gap-md space-y-sm text-xs">
                      {store.hostesses.map((hostess) => (
                        <li key={hostess.id}>
                          <Link className="gap-sm flex items-center" to={`/hostesses/${hostess.id}`}>
                            <figure
                              data-slot="avatar"
                              className="relative flex size-[1.25rem] flex-none overflow-hidden rounded border border-gray-300 bg-brand/10"
                            >
                                <img
                                src={hostess.avatar}
                                alt={hostess.name}
                                className="w-full h-full object-cover group-hover:opacity-60"
                              />
                            </figure>
                            <h2 className="truncate font-bold leading-snug">
                              {hostess.name}
                              {hostess.role && ` (${hostess.role})`}
                            </h2>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-gray-400">未設定</p>
                  )}
                </dd>
              </dl>

              {/* ボトル */}
              <dl className="space-y-sm col-span-4">
                <dt className="text-xs font-bold text-gray-500">キープボトル</dt>
                <dd className="leading-none tracking-tight">
                  {store.bottles.length > 0 ? (
                    <ul className="space-y-sm text-xs font-bold">
                      {store.bottles.map((bottle) => (
                        <li key={bottle.id} className="line-clamp-1">
                          <BottleWine className="size-[1.75em] fill-black text-warning" />
                          {bottle.name}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-gray-400">なし</p>
                  )}
                </dd>
              </dl>

              {/* 親元 */}
              <dl className="space-y-sm col-span-3">
                <dt className="text-xs font-bold text-gray-500">親元</dt>
                <dd>
                  {store.parents.length > 0 ? (
                    <ul className="space-y-xs text-xs">
                      {store.parents.map((parent, idx) => (
                        <li key={idx} className="space-x-xs">
                          <Link to={`/customers/${parent.customerId}`}>
                            {parent.customerName}
                          </Link>
                          <Link
                            className="text-xs font-normal tracking-tight text-inherit"
                            to={`/hostesses/${parent.hostessId}`}
                          >
                            {parent.hostessEmoji}
                            {parent.hostessName}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-gray-400">なし</p>
                  )}
                </dd>
              </dl>
            </div>
          </li>
        ))}
      </ul>

      {/* メモセクション */}
      <div className="space-y-sm">
        <dl className="space-y-sm p-md border">
          <dt className="text-xs font-bold text-gray-500">ホールメモ</dt>
          <dd className="text-xs leading-snug whitespace-pre-wrap">
            {data.memos.hallMemo || <span className="text-gray-400">未記入</span>}
          </dd>
        </dl>
        <div className="p-md border">
          <button type="button" onClick={() => setMemoDialogOpen(true)} className={`px-md py-sm -me-sm -mt-sm text-2xs float-end flex-none rounded-md ${Constants.btnWhite}`}>編集</button>
          <dl className="space-y-sm grow">
            <dt className="text-xs font-bold text-gray-500">ホステスメモ</dt>
            <dd className="text-xs leading-snug whitespace-pre-wrap">
              {data.memos.hostessMemo || <span className="text-gray-400">未記入</span>}
            </dd>
          </dl>
        </div>
        {hostess && (
          <HostessMemoEditSheet
            open={memoDialogOpen}
            onOpenChange={setMemoDialogOpen}
            customerId={data.id}
            hostessId={hostess.id}
            currentMemo={data.memos.hostessMemo || ''}
          />
        )}
      </div>

      {/* 名刺リスト（全カード表示） */}
      {data.businessCards.length > 0 && (
        <div className="space-y-md">
          <h3 className="text-lg font-bold">名刺 ({data.businessCards.length})</h3>

          {data.businessCards.map((card) => (
            <div key={card.id} className="space-y-md border-t pt-md">
              {/* 編集（自分がもらった名刺のみ表示されているので全カード編集可） */}
              {hostess && <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => { setEditCard(card); setEditOpen(true); }}
                  className="text-brand text-sm font-bold active:opacity-60"
                >
                  編集
                </button>
              </div>}
              {/* 名刺画像（タップで拡大） */}
              {(card.front || card.back) && (
                <div className="gap-sm grid grid-cols-2">
                  {card.front && (
                    <figure>
                      <button type="button" onClick={() => openViewer(card.front, card.back, 'front')} className="w-full">
                        <img src={card.front} className="w-full rounded border" alt="名刺表面" />
                      </button>
                      <figcaption className="text-xs text-center text-gray-500 mt-1">表面</figcaption>
                    </figure>
                  )}
                  {card.back && (
                    <figure>
                      <button type="button" onClick={() => openViewer(card.front, card.back, 'back')} className="w-full">
                        <img src={card.back} className="w-full rounded border" alt="名刺裏面" />
                      </button>
                      <figcaption className="text-xs text-center text-gray-500 mt-1">裏面</figcaption>
                    </figure>
                  )}
                </div>
              )}

              {/* 名刺情報 */}
              <dl className="space-y-sm text-sm">
                <div className="grid grid-cols-3 gap-md">
                  <dt className="text-gray-500">氏名</dt>
                  <dd className="col-span-2 font-bold">
                    <span className="inline-flex items-center gap-2">{card.name || '未設定'} <BusinessCardFieldMark row={fieldChecks.data?.get(String(card.id))} field="name" /></span>
                    {fieldReview(card, 'name')}
                  </dd>
                </div>
                {card.kana && (
                  <div className="grid grid-cols-3 gap-md">
                    <dt className="text-gray-500">よみがな</dt>
                    <dd className="col-span-2">{card.kana}</dd>
                  </div>
                )}
                {card.mobile && (
                  <div className="grid grid-cols-3 gap-md">
                    <dt className="text-gray-500">携帯</dt>
                    <dd className="col-span-2">{formatPhoneNumber(card.mobile)}</dd>
                  </div>
                )}
                {card.email && (
                  <div className="grid grid-cols-3 gap-md">
                    <dt className="text-gray-500">メール</dt>
                    <dd className="col-span-2">{card.email}</dd>
                  </div>
                )}
                {card.companyName && (
                  <div className="grid grid-cols-3 gap-md">
                    <dt className="text-gray-500">会社名</dt>
                    <dd className="col-span-2 font-bold">{card.companyName}</dd>
                  </div>
                )}
                {card.position && (
                  <div className="grid grid-cols-3 gap-md">
                    <dt className="text-gray-500">役職</dt>
                    <dd className="col-span-2">{card.position}</dd>
                  </div>
                )}
                {card.companyPhone && (
                  <div className="grid grid-cols-3 gap-md">
                    <dt className="text-gray-500">会社電話</dt>
                    <dd className="col-span-2">{formatPhoneNumber(card.companyPhone)}</dd>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-md">
                  <dt className="text-gray-500">住所</dt>
                  <dd className="col-span-2">
                    {card.postalCode && <span className="block">〒{card.postalCode}</span>}
                    <span className="inline-flex items-center gap-2">{card.companyAddress || '未設定'} <BusinessCardFieldMark row={fieldChecks.data?.get(String(card.id))} field="address" /></span>
                    {fieldReview(card, 'address')}
                  </dd>
                </div>
                {card.handwrittenMemo && (
                  <div className="grid grid-cols-3 gap-md">
                    <dt className="text-gray-500">手書きメモ</dt>
                    <dd className="col-span-2 whitespace-pre-wrap">{card.handwrittenMemo}</dd>
                  </div>
                )}
              </dl>
            </div>
          ))}
        </div>
      )}

      {/* 名刺画像ビューア */}
      {viewerState && (
        <BizcardImageViewer
          front={viewerState.front}
          back={viewerState.back}
          initialSide={viewerState.initialSide}
          onClose={() => setViewerState(null)}
        />
      )}

      {/* 名刺編集シート */}
      {editCard && (
        <BusinessCardEditSheet
          open={editOpen}
          onOpenChange={setEditOpen}
          card={editCard}
          customerId={data.id}
        />
      )}
    </div>
  );
};
